/**
 * Device Fingerprint Generator
 * Generates a stable device identifier without using cookies
 * Uses browser fingerprint + localStorage for persistence
 */

/**
 * Generate SHA-256 hash from string
 * @param {string} message - String to hash
 * @returns {Promise<string>} - Hex string of hash
 */
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Collect browser fingerprint data
 * @returns {string} - Concatenated fingerprint string
 */
function collectFingerprint() {
  const fingerprint = [
    navigator.userAgent || 'unknown',
    navigator.language || 'unknown',
    screen.width + 'x' + screen.height,
    screen.colorDepth || 'unknown',
    new Date().getTimezoneOffset(),
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    navigator.hardwareConcurrency || 'unknown',
    navigator.deviceMemory || 'unknown',
    navigator.platform || 'unknown'
  ].join('|');
  
  return fingerprint;
}

/**
 * Get or generate device ID
 * @returns {Promise<string>} - Device ID (UUID persistente)
 */
export async function getDeviceId() {
  const STORAGE_KEY = 'device_id';
  
  try {
    // Try to get from localStorage first
    let deviceId = localStorage.getItem(STORAGE_KEY);
    
    if (deviceId) {
      // Device ID already exists, return it
      console.log('[DEVICE_ID] ✅ Using EXISTING device ID from localStorage:', deviceId.substring(0, 12) + '...');
      console.log('[DEVICE_ID] Full ID:', deviceId);
      return deviceId;
    }
    
    // Generate new UUID (único e permanente)
    deviceId = crypto.randomUUID();
    
    // Store in localStorage for future use
    localStorage.setItem(STORAGE_KEY, deviceId);
    
    // Verify it was saved
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (savedId === deviceId) {
      console.log('[DEVICE_ID] ✅ NEW device ID generated and SAVED to localStorage:', deviceId.substring(0, 12) + '...');
      console.log('[DEVICE_ID] Full ID:', deviceId);
    } else {
      console.error('[DEVICE_ID] ❌ WARNING: Failed to save device ID to localStorage!');
    }
    
    return deviceId;
  } catch (error) {
    console.error('[DEVICE_ID] ❌ Error generating device ID:', error);
    
    // Fallback: generate a simple random ID
    const fallbackId = 'fallback_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now();
    localStorage.setItem(STORAGE_KEY, fallbackId);
    console.warn('[DEVICE_ID] ⚠️ Using fallback ID:', fallbackId);
    
    return fallbackId;
  }
}

/**
 * Clear device ID (for testing purposes)
 */
export function clearDeviceId() {
  localStorage.removeItem('device_id');
  console.log('[DEVICE_ID] Device ID cleared');
}
